import type { Entity } from '@backstage/catalog-model';

import type { WithoutIndexSignature } from './types';

import { notEmpty } from './notEmpty/notEmpty';

export type EntityFilter<T> = Partial<Record<EntityFilterKeys<T>, (string | symbol)[] | string | symbol>>;

export type EntityFilterKeys<T> = RecursiveKeys<WithoutIndexSignature<T>>;

export type TypedFilter<T extends Entity> = {
  filter: EntityFilter<T> | EntityFilter<T>[];
  predicate: (entity: Entity) => entity is T;
};

type RecursiveKeys<T> = T extends object
  ? {
      [K in keyof T]: T[K] extends unknown[]
        ? never
        : T[K] extends object
          ? RecursiveKeys<WithoutIndexSignature<T[K]>> extends infer R
            ? R extends string
              ? `${K & string}.${R}`
              : never
            : never
          : `${K & string}`;
    }[keyof T]
  : never;

export const isTypedFilter = <T extends Entity>(
  typedFilter: unknown,
): typedFilter is TypedFilter<T> =>
  !!typedFilter
  && typeof typedFilter === 'object'
  && 'filter' in typedFilter
  && 'predicate' in typedFilter
  && typeof typedFilter.predicate === 'function';

export const patchTypedFilter = <T extends Entity>(
  { filter, predicate }: TypedFilter<T>,
  patch: (EntityFilter<T> | null | undefined)[] | EntityFilter<T>,
): null | TypedFilter<T> => {
  let newFilter: EntityFilter<T> | EntityFilter<T>[];
  if (Array.isArray(patch)) {
    newFilter = patch.filter(notEmpty).map(p => ({ ...filter, ...p }));
    if (!newFilter.length) {
      return null;
    }
  } else {
    newFilter = {
      ...filter,
      ...patch,
    };
  }
  return {
    filter: newFilter,
    predicate,
  };
};
